// components/ResetPassword.tsx
// Componente para restablecer contraseña con token

import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import {
  Lock,
  Eye,
  EyeOff,
  CheckCircle2,
  AlertCircle,
  Loader2,
  ArrowLeft,
  Shield,
  Check,
  X
} from 'lucide-react';

const ResetPassword: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isVerifying, setIsVerifying] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);
  const [userName, setUserName] = useState('');
  const [message, setMessage] = useState('');
  const [isSuccess, setIsSuccess] = useState(false);

  // Validaciones de contraseña
  const passwordRequirements = {
    minLength: newPassword.length >= 6,
    hasMatch: newPassword === confirmPassword && newPassword.length > 0
  };

  const isPasswordValid = Object.values(passwordRequirements).every(Boolean);

  // Verificar token al cargar
  useEffect(() => {
    if (!token) {
      setIsVerifying(false);
      setTokenValid(false);
      setMessage('Token no válido');
      return;
    }

    verifyToken();
  }, [token]);

  const verifyToken = async () => {
    try {
      const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';
      const response = await fetch(`${API_URL}/api/auth/verify-reset-token/${token}`);
      const data = await response.json();

      if (response.ok && data.success) {
        setTokenValid(true);
        setUserName(data.data.nombre);
      } else {
        setTokenValid(false);
        setMessage(data.message || 'Token inválido o expirado');
      }
    } catch (error) {
      console.error('Error verificando token:', error);
      setTokenValid(false);
      setMessage('Error verificando el token');
    } finally {
      setIsVerifying(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!isPasswordValid) {
      setMessage('Por favor verifica que las contraseñas cumplan los requisitos');
      setIsSuccess(false);
      return;
    }

    setIsLoading(true);
    setMessage('');

    try {
      const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';
      const response = await fetch(`${API_URL}/api/auth/reset-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          token,
          newPassword
        }),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setMessage(data.message);
        setIsSuccess(true);
        
        // Redirigir al login después de 3 segundos
        setTimeout(() => {
          navigate('/login');
        }, 3000);
      } else {
        setMessage(data.message || 'Error al restablecer la contraseña');
        setIsSuccess(false);
      }
    } catch (error) {
      console.error('Error:', error);
      setMessage('Error de conexión. Intenta nuevamente.');
      setIsSuccess(false);
    } finally {
      setIsLoading(false);
    }
  };

  // Vista de carga mientras verifica el token
  if (isVerifying) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 flex items-center justify-center p-4">
        <div className="bg-white/40 backdrop-blur-md border border-white/20 rounded-2xl shadow-xl p-8 text-center max-w-md w-full">
          <Loader2 className="w-12 h-12 text-blue-600 animate-spin mx-auto mb-4" />
          <p className="text-gray-700 font-medium">Verificando token...</p>
        </div>
      </div>
    );
  }

  // Vista si el token no es válido
  if (!tokenValid) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-50 via-orange-50 to-pink-50 flex items-center justify-center p-4">
        <div className="max-w-md w-full">
          <div className="bg-white/40 backdrop-blur-md border border-white/20 rounded-2xl shadow-xl p-8 text-center">
            <div className="w-16 h-16 bg-gradient-to-br from-red-400 to-orange-500 rounded-full flex items-center justify-center mx-auto mb-4">
              <AlertCircle className="w-8 h-8 text-white" />
            </div>
            
            <h2 className="text-2xl font-bold text-gray-900 mb-2">
              Token Inválido
            </h2>
            <p className="text-gray-600 mb-6">
              {message || 'El enlace de recuperación es inválido o ha expirado.'}
            </p>
            
            <div className="space-y-3">
              <Link
                to="/forgot-password"
                className="block w-full bg-gradient-to-r from-blue-600 to-purple-600 text-white py-3 px-6 rounded-xl font-semibold hover:shadow-lg transition-all"
              >
                Solicitar nuevo enlace
              </Link>
              <Link
                to="/login"
                className="block w-full bg-white/60 backdrop-blur-sm border border-gray-200 text-gray-700 py-3 px-6 rounded-xl font-medium hover:bg-white/80 transition-all"
              >
                Volver al login
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Vista principal del formulario
  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="bg-white/40 backdrop-blur-md border border-white/20 rounded-2xl shadow-xl p-6 mb-6">
            <h1 className="text-4xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent mb-2">
              consultAR
            </h1>
            <p className="text-gray-600 text-sm">Tu plataforma de gestión profesional</p>
          </div>
          
          <div className="bg-white/40 backdrop-blur-md border border-white/20 rounded-2xl shadow-xl p-8">
            <div className="w-16 h-16 bg-gradient-to-br from-blue-400 to-purple-500 rounded-full flex items-center justify-center mx-auto mb-4">
              <Lock className="w-8 h-8 text-white" />
            </div>
            
            <h2 className="text-3xl font-bold text-gray-900 mb-2">
              Nueva Contraseña
            </h2>
            <p className="text-gray-600 text-sm">
              Hola {userName}, ingresa tu nueva contraseña
            </p>
          </div>
        </div>

        {/* Formulario */}
        <form className="space-y-6" onSubmit={handleSubmit}>
          {/* Campo de nueva contraseña */}
          <div className="bg-white/40 backdrop-blur-md border border-white/20 rounded-2xl shadow-xl p-6">
            <label htmlFor="newPassword" className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
              <Lock className="w-4 h-4" />
              Nueva Contraseña
            </label>
            <div className="relative">
              <input
                id="newPassword"
                name="newPassword"
                type={showNewPassword ? 'text' : 'password'}
                required
                className="w-full px-4 py-3 bg-white/50 backdrop-blur-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all pr-12"
                placeholder="Mínimo 6 caracteres"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                disabled={isLoading}
              />
              <button
                type="button"
                onClick={() => setShowNewPassword(!showNewPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
              >
                {showNewPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>
          </div>

          {/* Campo de confirmar contraseña */}
          <div className="bg-white/40 backdrop-blur-md border border-white/20 rounded-2xl shadow-xl p-6">
            <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
              <Shield className="w-4 h-4" />
              Confirmar Contraseña
            </label>
            <div className="relative">
              <input
                id="confirmPassword"
                name="confirmPassword"
                type={showConfirmPassword ? 'text' : 'password'}
                required
                className="w-full px-4 py-3 bg-white/50 backdrop-blur-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all pr-12"
                placeholder="Confirma tu contraseña"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                disabled={isLoading}
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
              >
                {showConfirmPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>
          </div>

          {/* Requisitos de contraseña */}
          {newPassword && (
            <div className="bg-white/40 backdrop-blur-md border border-white/20 rounded-2xl shadow-xl p-4">
              <p className="text-sm font-medium text-gray-700 mb-3">Requisitos:</p>
              <div className="space-y-2">
                <div className="flex items-center gap-2 text-sm">
                  {passwordRequirements.minLength ? (
                    <Check className="w-4 h-4 text-green-600" />
                  ) : (
                    <X className="w-4 h-4 text-red-600" />
                  )}
                  <span className={passwordRequirements.minLength ? 'text-green-700' : 'text-gray-600'}>
                    Mínimo 6 caracteres
                  </span>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  {passwordRequirements.hasMatch ? (
                    <Check className="w-4 h-4 text-green-600" />
                  ) : (
                    <X className="w-4 h-4 text-red-600" />
                  )}
                  <span className={passwordRequirements.hasMatch ? 'text-green-700' : 'text-gray-600'}>
                    Las contraseñas coinciden
                  </span>
                </div>
              </div>
            </div>
          )}

          {/* Mensaje de estado */}
          {message && (
            <div className={`bg-white/40 backdrop-blur-md border rounded-2xl shadow-xl p-4 ${
              isSuccess ? 'border-green-200' : 'border-red-200'
            }`}>
              <div className="flex items-start gap-3">
                {isSuccess ? (
                  <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                ) : (
                  <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                )}
                <div className="flex-1">
                  <p className={`text-sm font-medium ${
                    isSuccess ? 'text-green-800' : 'text-red-800'
                  }`}>
                    {message}
                  </p>
                  {isSuccess && (
                    <p className="text-sm text-green-700 mt-1">
                      Redirigiendo al login...
                    </p>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Botón de envío */}
          <button
            type="submit"
            disabled={isLoading || !isPasswordValid}
            className="w-full bg-gradient-to-r from-blue-600 to-purple-600 text-white py-4 px-6 rounded-xl font-semibold text-lg hover:shadow-xl transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {isLoading ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                Actualizando...
              </>
            ) : (
              <>
                <Lock className="w-5 h-5" />
                Restablecer Contraseña
              </>
            )}
          </button>

          {/* Link de navegación */}
          <div className="bg-white/40 backdrop-blur-md border border-white/20 rounded-2xl shadow-xl p-4 text-center">
            <Link
              to="/login"
              className="text-blue-600 hover:text-blue-800 text-sm font-medium transition-colors flex items-center justify-center gap-2"
            >
              <ArrowLeft className="w-4 h-4" />
              Volver al login
            </Link>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ResetPassword;